import {
  lerps,
  maxDotNumber,
  withinErrorMarginNumber,
  angle,
  distance
} from "@zyzgroup/core-common";
import { Matrix } from "../../../math";
import { YZCanvasContext } from "../canvas";
import {
  absolutizeSVGPath,
  normalizeSVGPath,
  parseSVGPath,
  SVGPathDataToD,
  type SVGParsedPathData,
  a2c,
  l2c,
  q2c
} from "../../../svg/";
import L from "./L";
import CubicBezier from "./CubicBezier";
import QuadraticBezier from "./QuadraticBezier";
import Arc from "./Arc";

// 连续的曲线，在坐标系的绝对值
export class PathD {
  // 在坐标系的绝对值
  public prevPathX = 0;
  public prevPathY = 0;

  // 提高性能, 没更新时使用缓存值
  private isUpdatedSVGPathData = true;
  private isUpdatedSVGPathD = true;
  private isUpdatedBoundingClientRect = true;
  private isUpdatedPolymorphPoints = true;
  private isUpdatedTotalLength = true;

  public normalizedLines: CubicBezier[] = [];

  // d = "M0,0"
  // d = [['C',1,2,3,4,5,6]]
  constructor(d?: string | SVGParsedPathData[]) {
    const data = typeof d == "string" ? parseSVGPath(d) : d || [];
    /// paths 只包含 M 或 C 语句
    const paths = normalizeSVGPath(absolutizeSVGPath(data));
    for (let i = 0, count = paths.length; i < count; i++) {
      const path = paths[i];
      const command = path[0];
      if (command == "M") {
        this.prevPathX = path[1];
        this.prevPathY = path[2];
      } else {
        this._appendLine(
          new CubicBezier({
            x: this.prevPathX,
            y: this.prevPathY,
            c1x: path[1],
            c1y: path[2],
            c2x: path[3],
            c2y: path[4],
            toX: path[5],
            toY: path[6]
          })
        );
      }
    }
  }

  private _appendLine(line: L) {
    this.isUpdatedSVGPathData = true;
    this.isUpdatedSVGPathD = true;
    this.isUpdatedBoundingClientRect = true;
    this.isUpdatedPolymorphPoints = true;
    this.isUpdatedTotalLength = true;
    if (line instanceof Arc) {
      const curves = a2c(
        this.prevPathX,
        this.prevPathY,
        line.toX,
        line.toY,
        line.largeArcFlag,
        line.sweepFlag,
        line.rx,
        line.ry,
        line.xAxisRotate
      );
      curves.forEach((curve) => {
        const [x0, y0, x1, y1, x2, y2, x, y] = curve;
        this.normalizedLines.push(
          new CubicBezier({
            x: this.prevPathX,
            y: this.prevPathY,
            c1x: x1,
            c1y: y1,
            c2x: x2,
            c2y: y2,
            toX: x,
            toY: y
          })
        );
        this.prevPathX = x;
        this.prevPathY = y;
      });
      return;
    }
    if (line instanceof CubicBezier) {
      this.normalizedLines.push(line.clone());
      this.prevPathX = line.toX;
      this.prevPathY = line.toY;
      return;
    }
    let x1, y1, x2, y2, toX, toY;
    if (line instanceof QuadraticBezier) {
      [x1, y1, x2, y2, toX, toY] = q2c(
        this.prevPathX,
        this.prevPathY,
        line.cx,
        line.cy,
        line.toX,
        line.toY
      );
    } else {
      // L
      [x1, y1, x2, y2, toX, toY] = l2c(
        this.prevPathX,
        this.prevPathY,
        line.toX,
        line.toY
      );
    }
    this.normalizedLines.push(
      new CubicBezier({
        x: this.prevPathX,
        y: this.prevPathY,
        c1x: x1,
        c1y: y1,
        c2x: x2,
        c2y: y2,
        toX: toX,
        toY: toY
      })
    );
    this.prevPathX = toX;
    this.prevPathY = toY;
  }

  public static fromPolymorphPoints(points: number[]): PathD {
    const d = new PathD();
    const count = points.length;
    if (count) {
      d.prevPathX = points[0];
      d.prevPathY = points[1];
      for (let k = 2; k + 5 < count; k += 6) {
        d._appendLine(
          new CubicBezier({
            x: d.prevPathX,
            y: d.prevPathY,
            c1x: points[k],
            c1y: points[k + 1],
            c2x: points[k + 2],
            c2y: points[k + 3],
            toX: points[k + 4],
            toY: points[k + 5]
          })
        );
      }
    }
    return d;
  }

  public static fromLines(lines: L[]): PathD {
    const d = new PathD();
    const count = lines.length;
    if (count) {
      d.prevPathX = lines[0].x;
      d.prevPathY = lines[0].y;
      for (let i = 0; i < count; i++) {
        d._appendLine(lines[i]);
      }
    }
    return d;
  }

  public clone(): PathD {
    return PathD.fromLines(this.normalizedLines);
  }

  // 在坐标系的绝对值
  private cachedSVGPathData: SVGParsedPathData[] = [];
  public toSVGPathData(): SVGParsedPathData[] {
    if (this.isUpdatedSVGPathData) {
      const data: SVGParsedPathData[] = [];
      for (let i = 0, count = this.normalizedLines.length; i < count; i++) {
        const line = this.normalizedLines[i];
        if (i == 0) {
          data.push(["M", line.x, line.y]);
        }
        data.push(...line.toSVGPathData());
      }
      // data.push(["z"]);
      this.cachedSVGPathData = data;
      this.isUpdatedSVGPathData = false;
    }
    return this.cachedSVGPathData;
  }

  // 外部使用
  private cachedSVGPathD = "";
  public toSVGPathD(): string {
    if (this.isUpdatedSVGPathD) {
      this.cachedSVGPathD = SVGPathDataToD(this.toSVGPathData());
      this.isUpdatedSVGPathD = false;
    }
    return this.cachedSVGPathD;
  }

  // 外部使用
  public toCanvasContext(ctx: YZCanvasContext) {
    ctx.beginPath();
    for (let i = 0, count = this.normalizedLines.length; i < count; i++) {
      const line = this.normalizedLines[i];
      if (i == 0) {
        ctx.moveTo(line.x, line.y);
      }
      line.toCanvasContext(ctx);
    }
    // ctx.closePath();
  }

  // 外部使用
  // 相对坐标系原点 - 坐标系没有考虑 transform : translate rotate scale
  private cachedBoundingClientRect = {
    x: 0,
    y: 0,
    width: 0,
    height: 0
  };
  public getBoundingClientRect(dot = 2) {
    if (this.isUpdatedBoundingClientRect) {
      const X: number[] = [];
      const Y: number[] = [];
      this.normalizedLines.forEach((l) => {
        const rect = l.getBoundingClientRect(dot);
        X.push(rect.x, rect.x + rect.width);
        Y.push(rect.y, rect.y + rect.height);
      });
      if (X.length == 0 || Y.length == 0) {
        this.cachedBoundingClientRect = { x: 0, y: 0, width: 0, height: 0 };
      } else {
        const xmin = Math.min(...X);
        const ymin = Math.min(...Y);
        const xmax = Math.max(...X);
        const ymax = Math.max(...Y);
        this.cachedBoundingClientRect = {
          x: maxDotNumber(xmin, dot),
          y: maxDotNumber(ymin, dot),
          width: maxDotNumber(xmax - xmin, dot),
          height: maxDotNumber(ymax - ymin, dot)
        };
      }
      this.isUpdatedBoundingClientRect = false;
    }
    return this.cachedBoundingClientRect;
  }

  // 相对坐标系原点 - 坐标系没有考虑 transform : translate rotate scale
  public getBoundingClientRectCenter(dot = 2) {
    const rect = this.getBoundingClientRect(dot);
    return {
      x: maxDotNumber(rect.x + rect.width * 0.5, dot),
      y: maxDotNumber(rect.y + rect.height * 0.5, dot)
    };
  }

  // 通过 DrawContext shape.m 获得 新的 PathD
  public transform(options: {
    m?: Matrix | DOMMatrix;
    x?: number;
    y?: number;
    centerX?: number;
    centerY?: number;
    degree?: number;
    scaleX?: number;
    scaleY?: number;
  }): PathD {
    let m: Matrix | DOMMatrix;
    if (options.m) {
      m = options.m;
    } else {
      // 移动的是坐标系
      // the order of these are important
      m = new Matrix();
      m.translate(options.x || 0, options.y || 0);
      m.rotate(options.degree || 0, options.centerX, options.centerY);
      m.scale(
        options.scaleX || 1,
        options.scaleY || 1,
        options.centerX,
        options.centerY
      );
    }
    if (!m.isIdentity) {
      return PathD.fromLines(
        this.normalizedLines.map((l) => {
          let x = l.x;
          let y = l.y;
          let c1x = l.c1x;
          let c1y = l.c1y;
          let c2x = l.c2x;
          let c2y = l.c2y;
          let toX = l.toX;
          let toY = l.toY;
          ({ x: x, y: y } = m.transformPoint({ x: x, y: y }));
          ({ x: c1x, y: c1y } = m.transformPoint({ x: c1x, y: c1y }));
          ({ x: c2x, y: c2y } = m.transformPoint({ x: c2x, y: c2y }));
          ({ x: toX, y: toY } = m.transformPoint({ x: toX, y: toY }));
          return new CubicBezier({ x, y, c1x, c1y, c2x, c2y, toX, toY });
        })
      );
    } else {
      return this.clone();
      // return this;
    }
  }

  // returns length in pixels
  private cachedTotalLength?: number;
  public getTotalLength(): number {
    if (
      !(typeof this.cachedTotalLength == "number") ||
      this.isUpdatedTotalLength
    ) {
      this.cachedTotalLength = Math.round(
        this.normalizedLines.reduce((acc, l) => (acc += l.length()), 0)
      ); // 实际测试值：1718.289461119151
      // const el = createSvgPath(this.toSVGPathD());
      // return el.getTotalLength(); // 实际测试值：1718.2906494140625
      this.isUpdatedTotalLength = false;
    }
    return this.cachedTotalLength;
  }

  // 获取插值方法1
  public getPointAtLength(length: number): {
    x: number;
    y: number;
    alpha: number; // degree: angle of derivative
  } {
    let len = 0;
    let l = 0;
    const linesCount = this.normalizedLines.length;
    for (let i = 0; i < linesCount; i++) {
      const p = this.normalizedLines[i];
      l = p.length();
      if (len + l >= length) {
        return p.getPointAtSegmentLength(length - len);
      }
      len += l;
    }
    return this.normalizedLines[linesCount - 1].getPointAtSegmentLength(l); // 实际测试值：{x: 69.12001660070015, y: 139.8368628028231}
    // const el = createSvgPath(this.toSVGPathD());
    // return el.getPointAtLength(length); // 实际测试值：{x: 69.12151336669922, y: 139.8350830078125}
  }

  // 获取插值方法2 t (0.0 to 1.0) : The fraction of the overall duration of the path
  public interpolate(t: number): { x: number; y: number } {
    const linesCount = this.normalizedLines.length;
    if (linesCount === 0) {
      return { x: 0, y: 0 };
    } else if (linesCount === 1) {
      return this.normalizedLines[0].interpolate(t);
    } else {
      let ratio = t * linesCount;
      let path = this.normalizedLines[0];
      ratio--;
      if (ratio > 1) {
        for (let i = 1; i < linesCount; i++) {
          path = this.normalizedLines[i];
          if (ratio > 1) {
            ratio -= 1;
          } else {
            break;
          }
        }
      }
      return path.interpolate(ratio);
    }
  }

  public getSubPathD(
    fromLength: number,
    toLength: number,
    margin = 1e-6
  ): PathD {
    const p = this.getSubPathDAtLength(toLength, true);
    if (withinErrorMarginNumber(margin)(fromLength, 0)) {
      return p;
    } else {
      return p.getSubPathDAtLength(fromLength, false);
    }
  }

  private getSubPathDAtLength(
    length: number, // 以 length 为点 一分为二
    getStart = false // 返回 一分为二的 前面一部分 还是 后面一部分
  ): PathD {
    const sp = new PathD();
    let len = 0;
    let isReached = false;
    for (let i = 0, ii = this.normalizedLines.length; i < ii; i++) {
      const line = this.normalizedLines[i];
      if (i == 0) {
        sp.prevPathX = line.x;
        sp.prevPathY = line.y;
      }
      const l = line.length();
      if (len + l > length && !isReached) {
        // 从 0 到 length 的 开头部分
        isReached = true;
        const point = line.getPointAtSegmentLength(length - len);
        sp._appendLine(
          new CubicBezier({
            x: sp.prevPathX,
            y: sp.prevPathY,
            c1x: point.start.x,
            c1y: point.start.y,
            c2x: point.m.x,
            c2y: point.m.y,
            toX: point.x,
            toY: point.y
          })
        );
        if (getStart) {
          return sp;
        }
        // 从 length 到 end 的 结尾部分
        sp.normalizedLines = [];
        sp._appendLine(
          new CubicBezier({
            x: sp.prevPathX,
            y: sp.prevPathY,
            c1x: point.n.x,
            c1y: point.n.y,
            c2x: point.end.x,
            c2y: point.end.y,
            toX: line.toX,
            toY: line.toY
          })
        );
        len += l;
        continue;
      }
      sp._appendLine(line);
      len += l;
    }
    return sp;
  }

  public closestPoint(
    pointX: number,
    pointY: number
  ): {
    point: {
      x: number;
      y: number;
    };
    rotation: number;
    distance: number;
  } {
    const pathLength = this.getTotalLength();
    let best = { x: pointX, y: pointY };
    let bestLength = 0;
    let bestDistance = Infinity;
    let precision = 8;

    // linear scan for coarse approximation
    for (
      let scanLength = 0;
      scanLength <= pathLength;
      scanLength += precision
    ) {
      const scan = this.getPointAtLength(scanLength);
      const scanDistance =
        (pointX - scan.x) * (pointX - scan.x) +
        (pointY - scan.y) * (pointY - scan.y);
      if (scanDistance < bestDistance) {
        best = scan;
        bestLength = scanLength;
        bestDistance = scanDistance;
      }
    }

    // binary search for precise estimate
    precision /= 2;
    while (precision > 0.5) {
      const beforeLength = bestLength - precision;
      const before = this.getPointAtLength(beforeLength);
      const beforeDistance =
        (pointX - before.x) * (pointX - before.x) +
        (pointY - before.y) * (pointY - before.y);
      const afterLength = bestLength + precision;
      const after = this.getPointAtLength(afterLength);
      const afterDistance =
        (pointX - after.x) * (pointX - after.x) +
        (pointY - after.y) * (pointY - after.y);
      if (beforeLength >= 0 && beforeDistance < bestDistance) {
        best = before;
        bestLength = beforeLength;
        bestDistance = beforeDistance;
      } else if (afterLength <= pathLength && afterDistance < bestDistance) {
        best = after;
        bestLength = afterLength;
        bestDistance = afterDistance;
      } else {
        precision /= 2;
      }
    }

    const p = this.getPointAtLength(
      bestLength + (bestLength === pathLength ? -0.1 : 0.1)
    );

    return {
      point: best,
      rotation:
        bestLength >= pathLength
          ? angle(p.x, p.y, best.x, best.y)
          : angle(best.x, best.y, p.x, p.y),
      distance: Math.sqrt(bestDistance)
    };
  }

  // 获得 Polymorph 要用的所有的点
  private cachedPolymorphPoints: number[] = [];
  public getPolymorphPoints(): number[] {
    if (this.isUpdatedPolymorphPoints) {
      if (this.normalizedLines.length) {
        const firstLine = this.normalizedLines[0];
        this.cachedPolymorphPoints = [firstLine.x, firstLine.y].concat(
          this.normalizedLines.reduce(
            (acc, l) => acc.concat([l.c1x, l.c1y, l.c2x, l.c2y, l.toX, l.toY]),
            [] as number[]
          )
        );
      }
      this.isUpdatedPolymorphPoints = false;
    }
    return [...this.cachedPolymorphPoints];
  }

  public getPolymorphPointsPerimeter(): number {
    let p = 0;
    const pts = this.getPolymorphPoints();
    const n = pts.length;
    if (n) {
      let x2 = pts[n - 2];
      let y2 = pts[n - 1];
      for (let i = 0; i < n; i += 6) {
        p += distance(pts[i], pts[i + 1], x2, y2);
        x2 = pts[i];
        y2 = pts[i + 1];
      }
    }
    return Math.floor(p);
  }

  // 扩充 PolymorphPoints 到对应数量的点，返回新 PathD
  public expendToPolymorphPoints(maxPoints: number[]): PathD {
    const maxCount = maxPoints.length;
    const ns = this.getPolymorphPoints();
    const nsLength = ns.length;
    if (nsLength) {
      const points: SVGParsedPathData[] = [];
      let needAddedCount = maxCount - nsLength;
      const ratio = Math.ceil(maxCount / nsLength);
      points.push(["M", ns[0], ns[1]]);
      let k = 2;
      while (k + 5 <= nsLength - 1) {
        const c1x = ns[k++];
        const c1y = ns[k++];
        const c2x = ns[k++];
        const c2y = ns[k++];
        const x = ns[k++];
        const y = ns[k++];
        points.push(["C", c1x, c1y, c2x, c2y, x, y]);
        let f = 0;
        while (f < ratio && needAddedCount > 0) {
          points.push(["C", x, y, x, y, x, y]);
          needAddedCount -= 6;
          f++;
        }
      }
      return new PathD(points);
    } else {
      return PathD.fromPolymorphPoints(maxPoints);
    }
  }

  // 返回 从 leftPathD 到 rightPathD 的 插值函数
  public static getInterpolator(
    left: PathD,
    right: PathD
  ): (
    t: number // t (0.0 to 1.0)
  ) => PathD {
    return (t: number) => {
      const m = withinErrorMarginNumber(Number.EPSILON);
      if (m(t, 0)) {
        return left.clone();
      }
      if (m(t, 1)) {
        return right.clone();
      }
      return PathD.fromPolymorphPoints(
        lerps(left.getPolymorphPoints(), right.getPolymorphPoints(), t).map(
          (n) => maxDotNumber(n, 2)
        )
      );
    };
  }
}

export default PathD;
